import React, { useState } from 'react';
import { FileText, Download, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import Layout from './Layout';

const MRVReportGenerator: React.FC = () => {
  const [reportType, setReportType] = useState('quarterly');
  const [generating, setGenerating] = useState(false);

  const handleGenerate = () => {
    setGenerating(true);
    setTimeout(() => setGenerating(false), 2000);
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FileText className="text-green-600" />
            AI-Powered MRV Report Generator
          </h1>
          <p className="text-gray-600 mt-2">
            Automated monitoring, reporting, and verification documentation
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Report Configuration */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Report Configuration</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Report Type
                  </label>
                  <select
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    <option value="monthly">Monthly Report</option>
                    <option value="quarterly">Quarterly Report (Gold Standard)</option>
                    <option value="annual">Annual Verification Report</option>
                    <option value="article6">Article 6 ITMO Report</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reporting Period
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="date"
                      defaultValue="2024-10-01"
                      className="px-4 py-2 border border-gray-300 rounded-lg"
                    />
                    <input
                      type="date"
                      defaultValue="2024-12-31"
                      className="px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Facilities
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" defaultChecked className="rounded" />
                      <span className="text-sm">Steel Plant - Bangalore</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" defaultChecked className="rounded" />
                      <span className="text-sm">Cement Plant - Chennai</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" defaultChecked className="rounded" />
                      <span className="text-sm">Waste Processing - Mumbai</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Verification Standard
                  </label>
                  <select className="w-full px-4 py-2 border border-gray-300 rounded-lg">
                    <option>Gold Standard</option>
                    <option>Verra VCS</option>
                    <option>ISO 14064-2</option>
                    <option>CDM Methodology</option>
                  </select>
                </div>

                <button
                  onClick={handleGenerate}
                  disabled={generating}
                  className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 flex items-center justify-center gap-2"
                >
                  {generating ? (
                    <>
                      <Clock className="animate-spin" size={20} />
                      Generating Report...
                    </>
                  ) : (
                    <>
                      <FileText size={20} />
                      Generate MRV Report
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Report Preview */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Report Preview</h2>

              <div className="space-y-4 text-sm">
                <div className="border-b pb-4">
                  <h3 className="font-bold text-lg mb-2">Executive Summary</h3>
                  <p className="text-gray-700">
                    Q4 2024 MRV Report for multi-facility operations demonstrating 12,847 tCO₂e
                    verified emission reductions through biogas integration, alternative fuel
                    adoption, and circular waste pathways.
                  </p>
                </div>

                <div className="border-b pb-4">
                  <h3 className="font-bold mb-2">Emission Reductions by Source</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Biogas in Steel Production</span>
                      <span className="font-medium">5,680 tCO₂e</span>
                    </div>
                    <div className="flex justify-between">
                      <span>RDF in Cement Kiln</span>
                      <span className="font-medium">4,320 tCO₂e</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Energy Efficiency</span>
                      <span className="font-medium">2,847 tCO₂e</span>
                    </div>
                  </div>
                </div>

                <div className="border-b pb-4">
                  <h3 className="font-bold mb-2">Verification Status</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="text-green-600" size={16} />
                      <span>Data completeness: 99.8%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="text-green-600" size={16} />
                      <span>CEMS calibration: Current</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="text-green-600" size={16} />
                      <span>Third-party audit: Passed</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-bold mb-2">Carbon Credit Eligibility</h3>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="text-2xl font-bold text-green-600 mb-1">12,847 ITMOs</div>
                    <div className="text-gray-700">
                      Eligible for Article 6 transfer | Estimated value: $2.4M
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Report Status & History */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Report Status</h2>

              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <CheckCircle className="text-green-600" size={24} />
                  <div>
                    <div className="font-medium text-gray-900">Q3 2024</div>
                    <div className="text-sm text-gray-600">Verified & Submitted</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg">
                  <Clock className="text-yellow-600" size={24} />
                  <div>
                    <div className="font-medium text-gray-900">Q4 2024</div>
                    <div className="text-sm text-gray-600">In Progress</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <AlertCircle className="text-gray-400" size={24} />
                  <div>
                    <div className="font-medium text-gray-900">Q1 2025</div>
                    <div className="text-sm text-gray-600">Not Started</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>

              <div className="space-y-2">
                <button className="w-full px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 flex items-center gap-2">
                  <Download size={16} />
                  Download Q3 Report (PDF)
                </button>
                <button className="w-full px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 flex items-center gap-2">
                  <Download size={16} />
                  Export Data (Excel)
                </button>
                <button className="w-full px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 flex items-center gap-2">
                  <FileText size={16} />
                  View Audit Trail
                </button>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-bold text-blue-900 mb-2">AI Insights</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Data quality improved 12% vs Q3</li>
                <li>• Zero critical gaps detected</li>
                <li>• Verification ready in 3 days</li>
                <li>• 98% automation achieved</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default MRVReportGenerator;
